import * as UserSchemas from "schemas/user"
import {
  createAssocClient,
  createUser,
  deleteAssocClient,
  getAcesByEnvironment,
  getAcesByWorkspace,
  getAssocEnvironments,
  resetPassword,
  updateAssocClient,
  updateUser,
} from "services/UserService"
import {deleteEntity, setEntities} from "redux/models/utils.model"
import {getCurrentEnvironmentId} from "redux/environment.selector"
import RootModel from "redux/models/index"
import {createModel} from "@rematch/core"
import {AxiosResponse} from "axios"
import {ACES, UAC, User, UserDtoSummaryWithAces} from "components/admin/user/UserManager.types"
import {updateUserAces} from "./user.utils";

export interface ModelUsers {
  "userWithAces": UserDtoSummaryWithAces[],
  "entities": {
    "users": any,
    "userAssocClients": any
  }
}

const initialState: ModelUsers = {
  userWithAces: [],
  entities: {
    users: {},
    userAssocClients: {},
  },
}

export default createModel<RootModel>()({
  state: initialState,
  reducers: {
    setEntities,
    deleteEntity,
    setAces(state: ModelUsers, userWithAces: UserDtoSummaryWithAces[]) {
      return {
        ...state,
        userWithAces: [
          ...state.userWithAces.filter(a => !userWithAces.find(newAces => newAces.id === a.id)),
          ...userWithAces
        ]
      }
    },
    loadAces(state: ModelUsers, userWithAces: UserDtoSummaryWithAces[]) {
      return {
        ...state,
        userWithAces
      }
    },
    'environment/cleanStates': () => {
      return initialState
    },
  },
  effects: (dispatch) => {
    return {
      async loadAssocClients(_, state): Promise<UAC[]> {
        const environmentId = getCurrentEnvironmentId(state)
        const assocClient = await getAssocEnvironments(environmentId)
        const normalized = UserSchemas.normalizeAssocClientResumes(assocClient)
        dispatch.users.setEntities(normalized.entities)
        return assocClient
      },
      async loadAcesByEnvironment(_, state): Promise<any> {
        const environmentId = getCurrentEnvironmentId(state)
        const userWithAces = await getAcesByEnvironment(environmentId)
        return dispatch.users.setAces(userWithAces)
      },
      async loadAcesByWorkspace({workspaceId}: { workspaceId: number }, state): Promise<any> {
        const userWithAces = await getAcesByWorkspace(workspaceId)
        return dispatch.users.setAces(userWithAces)
      },
      async createUser({data}: { data: User }, state) {
        const environmentId = getCurrentEnvironmentId(state)
        const userResponse = await createUser(data, environmentId)
        dispatch.users.loadAssocClients({})
        return userResponse
      },
      async createAssocClient({userEmail}: { userEmail: any }, state): Promise<UAC> {
        const environmentId = getCurrentEnvironmentId(state)
        return createAssocClient(userEmail, environmentId)
          .then(uac => {
            const normalized = UserSchemas.normalizeAssocClientResume(uac)
            dispatch.users.setEntities(normalized.entities)
            return uac
          })
      },
      async updateUser({id, data}: { id: any, data: any }, state): Promise<User> {
        const environmentId = getCurrentEnvironmentId(state)
        return updateUser(id, data, environmentId)
          .then(newData => {
            const normalized = UserSchemas.normalizeUserResume(newData)
            dispatch.users.setEntities(normalized.entities)
            return newData
          })
      },
      async updateUserAces({userId, aces}: { userId: any, aces: Pick<ACES, "target" | "roles">[] }, state): Promise<void> {
        const environmentId = getCurrentEnvironmentId(state)
        const currentUser = state.users.userWithAces.find(a => a.id === userId)

        if (currentUser) {
          const userWithAces = await updateUserAces({userId, aces}, currentUser, environmentId)
          if (userWithAces) {
            dispatch.users.setAces([
              userWithAces
            ])
          }
        }
      },
      async updateAssocClientActive({uac, active}: {
        forBatch?: boolean
        uac: { id: number | string },
        active: boolean
      }): Promise<any> {
        const normalized = UserSchemas.normalizeAssocClientResume(await updateAssocClient(uac.id, {active}))
        return dispatch.users.setEntities(normalized.entities)
      },
      async resetPassword({id}: { id: number | string }, state): Promise<AxiosResponse<any>> {
        const environmentId = getCurrentEnvironmentId(state)
        return resetPassword(id, environmentId)
      },
      async deleteAssocClient({uac}: {
        forBatch?: boolean
        uac: { id: string | number }
      }): Promise<any> {
        await deleteAssocClient(uac.id)
        return dispatch.users.deleteEntity({
          type: 'userAssocClients',
          id: uac.id
        })
      },
    }
  },
})
